import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close,
  Search,
  Clear,
  ExpandMore,
  ChevronRight,
} from "@mui/icons-material";
import { debounce } from "@mui/material/utils";
import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname } from "../../../utils";

interface Segment {
  id: string;
  externalId: number;
  name: string;
  category: string | null;
  subcategory: string | null;
  isB2b: boolean;
  description: string | null;
  similarity?: number;
  relevance?: number;
}

interface Subcategory {
  name: string;
  count: number;
}

interface Category {
  category: string;
  totalCount: number;
  subcategories: Subcategory[];
}

interface SegmentSearchDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  companyId: string;
  selectedSegments: { id: number; name: string }[];
  onSegmentsChange: (segments: { id: number; name: string }[]) => void;
}

const SegmentSearchDialog: React.FC<SegmentSearchDialogProps> = ({
  open,
  onClose,
  getToken,
  companyId,
  selectedSegments,
  onSegmentsChange,
}) => {
  // Search state - use ref for input value to avoid re-renders on every keystroke
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<Segment[]>([]);
  const [hasSearchText, setHasSearchText] = useState(false);
  const searchQueryRef = useRef("");

  // Category state
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Local selection state (to apply on confirm)
  const [localSelectedSegments, setLocalSelectedSegments] = useState<
    { id: number; name: string }[]
  >([]);

  // Ref for search input to auto-focus
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Initialize local selection when dialog opens
  useEffect(() => {
    if (open) {
      setLocalSelectedSegments([...selectedSegments]);
      // Reset search state
      if (searchInputRef.current) {
        searchInputRef.current.value = "";
      }
      searchQueryRef.current = "";
      setHasSearchText(false);
      setSearchResults([]);
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      loadCategories();
      // Focus the search input after a brief delay to ensure dialog is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open, selectedSegments]);

  // Load categories on mount
  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/integrations/audience/segments/categories`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCategories(response.data.data || []);
    } catch (err) {
      console.error("Failed to load categories:", err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Use refs to avoid recreating the debounced function
  const getTokenRef = useRef(getToken);
  const companyIdRef = useRef(companyId);

  useEffect(() => {
    getTokenRef.current = getToken;
    companyIdRef.current = companyId;
  }, [getToken, companyId]);

  // Search segments
  const performSearch = useCallback(
    async (query: string, category: string | null, subcategory: string | null) => {
      if (!query && !category && !subcategory) {
        setSearchResults([]);
        return;
      }

      setSearchLoading(true);
      try {
        const token = await getTokenRef.current();
        const response = await axios.get(
          `${hostname}/integrations/audience/segments/semantic-search`,
          {
            params: {
              companyId: companyIdRef.current,
              q: query,
              category: category || undefined,
              subcategory: subcategory || undefined,
              limit: 50,
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSearchResults(response.data.data || []);
      } catch (err) {
        console.error("Failed to search segments:", err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [] // No dependencies - uses refs
  );

  // Debounced search - stable reference since performSearch doesn't change
  const debouncedSearch = useMemo(
    () => debounce((query: string, category: string | null, subcategory: string | null) => {
      performSearch(query, category, subcategory);
    }, 500),
    [performSearch]
  );

  // Handle search input change - no state update on every keystroke, just debounce the API call
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      searchQueryRef.current = value;
      // Only update hasSearchText state (for clear button visibility)
      const hasText = value.length > 0;
      if (hasText !== hasSearchText) {
        setHasSearchText(hasText);
      }
      debouncedSearch(value, selectedCategory, selectedSubcategory);
    },
    [debouncedSearch, selectedCategory, selectedSubcategory, hasSearchText]
  );

  // Toggle category expansion
  const toggleCategoryExpand = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Handle category selection
  const handleCategorySelect = (category: string | null) => {
    setSelectedCategory(category);
    setSelectedSubcategory(null);
    performSearch(searchQueryRef.current, category, null);
  };

  // Handle subcategory selection
  const handleSubcategorySelect = (category: string, subcategory: string) => {
    setSelectedCategory(category);
    setSelectedSubcategory(subcategory);
    performSearch(searchQueryRef.current, category, subcategory);
  };

  // Clear category/subcategory filters
  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    performSearch(searchQueryRef.current, null, null);
  };

  // Toggle segment selection
  const toggleSegment = (segment: Segment) => {
    const segmentData = { id: segment.externalId, name: segment.name };
    const isSelected = localSelectedSegments.some(
      (s) => s.id === segment.externalId
    );

    if (isSelected) {
      setLocalSelectedSegments(
        localSelectedSegments.filter((s) => s.id !== segment.externalId)
      );
    } else {
      setLocalSelectedSegments([...localSelectedSegments, segmentData]);
    }
  };

  // Check if segment is selected
  const isSegmentSelected = (segment: Segment) => {
    return localSelectedSegments.some((s) => s.id === segment.externalId);
  };

  // Remove selected segment chip
  const removeSelectedSegment = (segmentId: number) => {
    setLocalSelectedSegments(
      localSelectedSegments.filter((s) => s.id !== segmentId)
    );
  };

  // Apply selections and close
  const handleApply = () => {
    onSegmentsChange(localSelectedSegments);
    onClose();
  };

  // Clear search
  const handleClearSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    searchQueryRef.current = "";
    setHasSearchText(false);
    setSearchResults([]);
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.clear();
    };
  }, [debouncedSearch]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Search Audience Segments
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: "flex", height: "70vh" }}>
        {/* Left sidebar - Categories */}
        <Box
          sx={{
            width: 280,
            minWidth: 280,
            borderRight: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Categories
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {categoriesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List dense disablePadding>
                <ListItemButton
                  selected={selectedCategory === null && selectedSubcategory === null}
                  onClick={() => handleCategorySelect(null)}
                >
                  <ListItemText
                    primary="All Categories"
                    primaryTypographyProps={{
                      fontWeight: selectedCategory === null ? 600 : 400,
                    }}
                  />
                </ListItemButton>
                {categories.map((cat) => (
                  <React.Fragment key={cat.category}>
                    <ListItemButton
                      selected={selectedCategory === cat.category && selectedSubcategory === null}
                      onClick={() => {
                        if (cat.subcategories.length > 0) {
                          toggleCategoryExpand(cat.category);
                        }
                        handleCategorySelect(cat.category);
                      }}
                      sx={{ pr: 1 }}
                    >
                      {cat.subcategories.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategoryExpand(cat.category);
                          }}
                          sx={{ mr: 0.5, p: 0.25 }}
                        >
                          {expandedCategories.has(cat.category) ? (
                            <ExpandMore fontSize="small" />
                          ) : (
                            <ChevronRight fontSize="small" />
                          )}
                        </IconButton>
                      )}
                      <ListItemText
                        primary={cat.category}
                        secondary={`${cat.totalCount} segments`}
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight:
                            selectedCategory === cat.category && selectedSubcategory === null
                              ? 600
                              : 400,
                          noWrap: true,
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.75rem",
                        }}
                        sx={{ ml: cat.subcategories.length === 0 ? 3.5 : 0 }}
                      />
                    </ListItemButton>
                    {cat.subcategories.length > 0 && (
                      <Collapse in={expandedCategories.has(cat.category)}>
                        <List dense disablePadding>
                          {cat.subcategories.map((sub) => (
                            <ListItemButton
                              key={`${cat.category}-${sub.name}`}
                              selected={
                                selectedCategory === cat.category &&
                                selectedSubcategory === sub.name
                              }
                              onClick={() => handleSubcategorySelect(cat.category, sub.name)}
                              sx={{ pl: 5 }}
                            >
                              <ListItemText
                                primary={`${sub.name} (${sub.count})`}
                                primaryTypographyProps={{
                                  fontSize: "0.8rem",
                                  fontWeight:
                                    selectedSubcategory === sub.name ? 600 : 400,
                                  noWrap: true,
                                }}
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    )}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Box>

        {/* Right content - Search and Results */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Search bar */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search segments by name or description..."
              defaultValue=""
              onChange={handleSearchChange}
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: hasSearchText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {(selectedCategory || selectedSubcategory) && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {selectedCategory && (
                  <Chip
                    label={selectedCategory}
                    size="small"
                    onDelete={clearFilters}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {selectedSubcategory && (
                  <Chip
                    label={selectedSubcategory}
                    size="small"
                    onDelete={() => {
                      setSelectedSubcategory(null);
                      performSearch(searchQueryRef.current, selectedCategory, null);
                    }}
                    color="primary"
                  />
                )}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Selected segments */}
          {localSelectedSegments.length > 0 && (
            <>
              <Box sx={{ p: 2, pb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Selected ({localSelectedSegments.length})
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {localSelectedSegments.map((segment) => (
                    <Chip
                      key={segment.id}
                      label={segment.name}
                      size="small"
                      onDelete={() => removeSelectedSegment(segment.id)}
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Stack>
              </Box>
              <Divider />
            </>
          )}

          {/* Results */}
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {searchLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : searchResults.length > 0 ? (
              <Stack spacing={1}>
                {searchResults.map((segment) => (
                  <Paper
                    key={segment.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      backgroundColor: isSegmentSelected(segment)
                        ? "action.selected"
                        : "background.paper",
                      "&:hover": {
                        backgroundColor: isSegmentSelected(segment)
                          ? "action.selected"
                          : "action.hover",
                      },
                    }}
                    onClick={() => toggleSegment(segment)}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Checkbox
                        checked={isSegmentSelected(segment)}
                        size="small"
                        sx={{ p: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{
                            wordBreak: "break-word",
                          }}
                        >
                          {segment.name}
                        </Typography>
                        {segment.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mt: 0.5,
                            }}
                          >
                            {segment.description}
                          </Typography>
                        )}
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ mt: 0.5 }}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {segment.category && (
                            <Chip
                              label={segment.category}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                          {segment.subcategory && (
                            <Chip
                              label={segment.subcategory}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                          {segment.isB2b && (
                            <Chip
                              label="B2B"
                              size="small"
                              color="primary"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                          {segment.similarity !== undefined && (
                            <Chip
                              label={`${Math.round(segment.similarity * 100)}% match`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            ) : hasSearchText || selectedCategory || selectedSubcategory ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">
                  No segments found. Try a different search term or category.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">
                  Search for segments by name or select a category to browse.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
          {localSelectedSegments.length} segment{localSelectedSegments.length !== 1 ? "s" : ""} selected
        </Typography>
        <LoadingButton onClick={onClose} color="inherit">
          Cancel
        </LoadingButton>
        <LoadingButton onClick={handleApply} variant="contained" color="primary">
          Apply
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SegmentSearchDialog;
